import { get, isEmpty } from 'lodash';
import { Trans } from 'react-i18next';
import i18next from 'i18next';
import moment from 'moment-timezone';
import React from 'react';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import {
  Container,
  InputContainer,
  SelectorContainer,
  Divider,
  ToggleContainer,
  ActualCompoContainer,
} from '@commons/Modals/InputContentModal/styledComponents';
import { Dropdown, ToggleSwitch } from '@commons/utils/styledLibraryComponents';

import DateRangeSelectionModal from '@backoffice/components/DateRangeSelectionModal';

export const ANALYTICS_TYPE = {
  ORDER: 'ORDER',
  PRODUCTION: 'PRODUCTION',
};

const RELAUNCH_ORDER_MAX_PAST_YEARS = 2;

const SelectionModal = (props) => {
  const {
    storeName,
    stores,
    selectedStores,
    setDateRange,
    onModalSelectionChange,
    maxPastDate,
    clientRecipes,
    useCurrentComposition,
    selectedRecipes,
  } = props;

  return (
    <Container>
      <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}>
        <Trans
          components={{ bold: <strong /> }}
          i18nKey="BACKOFFICE.ANALYTICS.RELAUNCH_MODAL_DESCRIPTION"
        />
      </Text>
      <SelectorContainer>
        <InputContainer>
          <Dropdown
            customListStyle={{
              position: 'fixed',
              top: 'inherit',
              left: 'inherit',
            }}
            iconSrc={
              selectedStores.length
                ? '/images/inpulse/pin-black-small.svg'
                : '/images/inpulse/pin-dmgrey-small.svg'
            }
            isRequired={true}
            isUniqueSelection={false}
            items={stores}
            label={storeName}
            selectedItems={selectedStores}
            tooltipText={i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_MODAL_STORES_TOOLTIP')}
            onSelectionChange={(selectedItems) => onModalSelectionChange('stores', selectedItems)}
          />
        </InputContainer>

        <Container style={{ marginTop: '4px' }}>
          <InputContainer>
            <DateRangeSelectionModal
              labelText={i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_MODAL_RANGE_DATE_TEXT')}
              maxPastDate={maxPastDate}
              setDateRange={setDateRange}
              timezone={'Europe/Paris'} // TODO - [TIMEZONES]
              tooltipText={i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_MODAL_RANGE_DATE_TOOLTIP')}
            />
          </InputContainer>
        </Container>
      </SelectorContainer>

      <Divider />
      <Text color={ENUM_COLORS.DARKER} font={ENUM_FONTS.TEXT_BIG_HEIGHT_24}>
        {i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_MODAL_ACTUAL_COMPOSITION_TEXT')}
      </Text>
      <ActualCompoContainer>
        <ToggleContainer>
          <ToggleSwitch
            checked={useCurrentComposition}
            handleClick={() =>
              onModalSelectionChange('useCurrentComposition', !useCurrentComposition)
            }
            id="useCurrentComposition"
          />
          <Text font={ENUM_FONTS.TEXT_MIDDLE_NORMAL} style={{ padding: '4px 8px' }}>
            {i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_MODAL_USE_ACTUAL_COMPO_TOGGLE')}
          </Text>
        </ToggleContainer>
        <InputContainer>
          <Dropdown
            customDropdownListStyle={{ maxHeight: '144px' }}
            customStyle={{ position: 'inherit' }}
            height={'64px'}
            iconSrc={
              clientRecipes.length && useCurrentComposition
                ? '/images/inpulse/burger-black-small.svg'
                : '/images/inpulse/burger-dmgrey-small.svg'
            }
            isDisabled={!useCurrentComposition}
            isUniqueSelection={false}
            items={clientRecipes}
            label={i18next.t('GENERAL.RECIPE_PLURAL')}
            selectedItems={selectedRecipes}
            largeDropdownList
            onSelectionChange={(selectedItems) =>
              onModalSelectionChange('selectedRecipes', selectedItems)
            }
          />
        </InputContainer>
      </ActualCompoContainer>
    </Container>
  );
};

export const getRelaunchAnalyticsModalParams = ({
  type,
  stores,
  clientRecipes,
  useCurrentComposition,
  setUseCurrentComposition,
  storeName,
  handleSave,
  setDateRange,
  selectedStores,
  dateRange = {},
  setSelectedStores,
  selectedRecipes,
  setSelectedRecipes,
}) => {
  /**
   * Check whether the form is valid
   *
   * @returns {Boolean} Whether the form is valid
   */
  const isFormDisabled = () => {
    // Check type of analytics to relaunch
    if (!type || !Object.values(ANALYTICS_TYPE).includes(type)) {
      return true;
    }

    // Check if some stores are selected
    if (isEmpty(selectedStores)) {
      return true;
    }

    if (useCurrentComposition && isEmpty(selectedRecipes)) {
      return true;
    }

    // Check if range of dates is properly set
    return !get(dateRange, 'endDate') || !get(dateRange, 'startDate');
  };

  /**
   * Handle the update of selected values in the modal
   *
   * @param {uuid} clientId - The client id on which retrieve stores
   *
   * @returns {Promise<Store[]>} The list of stores associated to the client
   */
  const onModalSelectionChange = (property, values) => {
    if (property === 'stores') {
      setSelectedStores(values);

      return;
    }

    if (property === 'useCurrentComposition') {
      setUseCurrentComposition(values);

      return;
    }

    if (property === 'selectedRecipes') {
      setSelectedRecipes(values);

      return;
    }
  };

  /**
   * Reset Relaunch Analytics states being used in form
   *
   * @returns {void}
   */
  const resetForm = async () => {
    setDateRange(null);
    setSelectedStores([]);
    setSelectedRecipes([]);
    setUseCurrentComposition(false);
  };

  const activeStores = stores.filter(({ active }) => active);

  // Restrict the relaunch period to the last 2 years for cost-matiere analytics only.
  // Production analytics keep no past-date limit (maxPastDate stays undefined).
  const maxPastDate =
    type === ANALYTICS_TYPE.ORDER
      ? moment.tz('Europe/Paris').subtract(RELAUNCH_ORDER_MAX_PAST_YEARS, 'years')
      : undefined;

  return {
    type: 'action',
    width: '542px',
    height: 'auto',
    icon: '/images/inpulse/sync-black.svg',
    title:
      type === ANALYTICS_TYPE.PRODUCTION
        ? i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_PRODUCTION_MODAL_TITLE')
        : i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_ORDER_MODAL_TITLE'),
    component: SelectionModal,
    data: {
      type,
      stores: activeStores,
      clientRecipes,
      useCurrentComposition,
      setUseCurrentComposition,
      storeName,
      setDateRange,
      selectedStores,
      selectedRecipes,
      setSelectedRecipes,
      onModalSelectionChange,
      maxPastDate,
    },
    handleCloseCleanUp: resetForm,
    actions: [
      {
        key: 0,
        color: 'inpulse-outline',
        label: i18next.t('GENERAL.CANCEL'),
        icon: '/images/inpulse/close-black-small.svg',
      },
      {
        key: 1,
        color: 'inpulse-default',
        label: i18next.t('BACKOFFICE.ANALYTICS.RELAUNCH_MODAL_RELAUNCH_BUTTON'),
        icon: '/images/inpulse/save-white-small.svg',
        handleClick: () => {
          handleSave(type);
          resetForm();
        },
        isDisabled: isFormDisabled(),
      },
    ],
  };
};

export default {
  ANALYTICS_TYPE,
  getRelaunchAnalyticsModalParams,
};
